import React, { useEffect, useState, useMemo, useRef } from "react";
import { JobDescriptionEntity } from "../../Entities/JobDescription";
import {
  Table,
  TableColumnsType,
  Button,
  Pagination,
  Skeleton,
  Modal,
  DatePicker,
  Space,
  Flex,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import jobDescriptionService from "../../Service/job.description.service";
import { Switch } from "antd";
import { openNotification } from "../../Components/Notification";
import useAccess from "../../Hooks/useAccess";
import { JobPostsPagination } from "../../Interface";
import dayjs from "dayjs";
import { CalendarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import SubscriptionWarning from "../../Components/SubscriptionWarning";

interface JobPostTableProps {
  jobPosts: JobDescriptionEntity[];
  fetchJobPosts: () => Promise<void>;
  pagination: JobPostsPagination;
  setPagination: (
    updater:
      | JobPostsPagination
      | ((prev: JobPostsPagination) => JobPostsPagination),
  ) => void;
}

const JobPostTable: React.FC<JobPostTableProps> = ({
  jobPosts,
  fetchJobPosts,
  pagination,
  setPagination,
}) => {
  const {
    currentLimits,
    currentSubscription,
    currentPlan,
    changeSubscriptionMeasurements,
  } = useAccess();
  const [open, setOpen] = useState<boolean>(false);
  const [tableScrollHeight, setTableScrollHeight] = useState(400);
  const containerRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [togglingJobId, setTogglingJobId] = useState<string | null>(null);
  const [endDateModalVisible, setEndDateModalVisible] =
    useState<boolean>(false);
  const [jobToReactivate, setJobToReactivate] =
    useState<JobDescriptionEntity | null>(null);
  const [newEndDate, setNewEndDate] = useState<dayjs.Dayjs | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOnUpdate = () => fetchJobPosts();
    jobDescriptionService.subscribe("JOB_POST_ADDED", fetchOnUpdate);
    return () => {
      jobDescriptionService.unsubscribe("JOB_POST_ADDED", fetchOnUpdate);
    };
  }, []);

  const toggleJobStatus = async (
    job: JobDescriptionEntity,
    status: boolean,
  ) => {
    if (currentSubscription?.status === "inactive") {
      openNotification(
        "warning",
        "Upgrade Required",
        "Please upgrade your pack to enjoy your benefits.",
      );
      return;
    }
    let query = status ? "publish" : "unpublish";
    if (
      query === "publish" &&
      currentSubscription &&
      currentPlan &&
      currentLimits?.jobPosts !== undefined &&
      currentLimits?.jobPosts !== -1
    ) {
      if (
        currentSubscription?.measurements.jobPosts >= currentLimits?.jobPosts
      ) {
        return setOpen(true);
      }
    }

    // Check if reactivating a job post with expired end date
    if (query === "publish" && job.endDate) {
      const endDate = dayjs(job.endDate);
      const today = dayjs().startOf("day");

      if (endDate.isBefore(today)) {
        // End date has passed, prompt for extension
        setJobToReactivate(job);
        setNewEndDate(dayjs().add(30, "days")); // Default to 30 days from now
        setEndDateModalVisible(true);
        return;
      }
    }

    setTogglingJobId(job._id);
    try {
      await jobDescriptionService.updateJobDescriptionStatus(job._id, query);
      if (query === "publish") {
        changeSubscriptionMeasurements(1, "jobPosts");
      } else {
        changeSubscriptionMeasurements(-1, "jobPosts");
      }
      await fetchJobPosts();
      openNotification("success", "Job Post Updated", "Successfully Updated");
    } finally {
      setTogglingJobId(null);
    }
  };

  const handleExtendEndDate = async () => {
    if (!jobToReactivate || !newEndDate) {
      openNotification("error", "Error", "Please select a valid end date");
      return;
    }

    const wasAlreadyActive = jobToReactivate.isActive;
    setTogglingJobId(jobToReactivate._id);
    try {
      const updatedJob = {
        ...jobToReactivate,
        endDate: newEndDate.format("YYYY-MM-DD"),
        isActive: true,
      };

      await jobDescriptionService.updateJobDescriptionV2(
        updatedJob as any,
        jobToReactivate._id,
      );

      // Then activate the job
      await jobDescriptionService.updateJobDescriptionStatus(
        jobToReactivate._id,
        "publish",
      );

      changeSubscriptionMeasurements(1, "jobPosts");
      await fetchJobPosts();

      setEndDateModalVisible(false);
      setJobToReactivate(null);
      setNewEndDate(null);

      openNotification(
        "success",
        wasAlreadyActive ? "End Date Updated" : "Job Post Reactivated",
        wasAlreadyActive
          ? `End date has been extended to ${newEndDate.format("DD-MM-YYYY")}.`
          : `Job post has been reactivated with new end date: ${newEndDate.format("DD-MM-YYYY")}`,
      );
    } catch (error: any) {
      openNotification(
        "error",
        "Error",
        error?.response?.data?.message ||
          (wasAlreadyActive
            ? "Failed to update end date"
            : "Failed to reactivate job post"),
      );
    } finally {
      setTogglingJobId(null);
    }
  };

  const handleCancelEndDateModal = () => {
    setEndDateModalVisible(false);
    setJobToReactivate(null);
    setNewEndDate(null);
  };

  const navigateToTab = (
    _id: string,
    tab:
      | "details"
      | "applications"
      | "round-qualified"
      | "pending-interview"
      | "hired"
      | "rejected",
  ) => {
    let params = new URLSearchParams();

    switch (tab) {
      case "details":
        params.set("tabIndex", "details");
        break;
      case "applications":
        params.set("tabIndex", "applications");
        break;
      case "round-qualified":
        params.set("tabIndex", "applications");
        params.set("roundStatus", "qualified");
        break;
      case "pending-interview":
        params.set("tabIndex", "applications");
        params.set("interviewStatus", "scheduled");
        break;
      case "hired":
        params.set("tabIndex", "applications");
        params.set("status", "hired");
        break;
      case "rejected":
        params.set("tabIndex", "applications");
        params.set("status", "rejected");
        break;
      default:
        params.set("tabIndex", "details");
        break;
    }
    navigate(`/job-posts/${_id}?${params.toString()}`);
  };

  // Helper to check if a row is currently being toggled
  const isRowToggling = (record: JobDescriptionEntity) =>
    togglingJobId === record._id;

  // Helper to check if job post end date is within 3 days (expiring soon). Only for active posts with valid end date.
  const isEndDateNear = (record: JobDescriptionEntity) => {
    if (!record.isActive || !record.endDate) return false;
    const end = dayjs(record.endDate);
    if (!end.isValid()) return false;
    const endOfDay = end.endOf("day");
    const now = dayjs();
    const daysUntilEnd = endOfDay.diff(now, "day");
    return daysUntilEnd >= 0 && daysUntilEnd <= 3;
  };

  const hasValidEndDate = (record: JobDescriptionEntity) =>
    !!record.endDate && dayjs(record.endDate).isValid();

  // Edge case: end date has passed (used to detect active-but-expired jobs from API)
  const isPastEndDate = (record: JobDescriptionEntity) => {
    if (!hasValidEndDate(record)) return false;
    return dayjs(record.endDate).endOf("day").isBefore(dayjs().startOf("day"));
  };

  // Active but end date has passed – recruiter should extend or deactivate
  const isActiveButPastEndDate = (record: JobDescriptionEntity) =>
    record.isActive && isPastEndDate(record);

  // Inactive because end date has passed (backend auto-deactivated)
  const isInactiveButPastEndDate = (record: JobDescriptionEntity) =>
    !record.isActive && isPastEndDate(record);

  const openExtendEndDateModal = (record: JobDescriptionEntity) => {
    setJobToReactivate(record);
    setNewEndDate(dayjs().add(30, "days"));
    setEndDateModalVisible(true);
  };

  // Skeleton component for table cells
  const CellSkeleton = ({ width = 60 }: { width?: number }) => (
    <Skeleton.Input active size="small" style={{ width, minWidth: width }} />
  );

  const columns: TableColumnsType<JobDescriptionEntity> = [
    {
      key: "createdAt",
      title: "Posted",
      dataIndex: "createdAt",
      width: "15%",
      minWidth: 120,
      render: (text, record) =>
        isRowToggling(record) ? (
          <CellSkeleton width={80} />
        ) : (
          // dayjs(record.createdAt).format("DD MMM YYYY")
          <Flex vertical gap={4}>
            <Space size="small">
              <CalendarOutlined style={{ fontSize: "12px" }} />
              <Typography.Text style={{ fontSize: "12px" }}>
                {dayjs(record.createdAt).format("DD MMM YYYY")}
              </Typography.Text>
            </Space>
            {hasValidEndDate(record) &&
              (isInactiveButPastEndDate(record) ? (
                <Space size="small">
                  <ClockCircleOutlined
                    style={{
                      fontSize: "12px",
                      color: "var(--ant-color-warning)",
                    }}
                  />
                  <Typography.Text type="warning" style={{ fontSize: "12px" }}>
                    Expired on: {dayjs(record.endDate).format("DD MMM YYYY")}
                  </Typography.Text>
                </Space>
              ) : (
                <Space size="small">
                  <ClockCircleOutlined style={{ fontSize: "12px" }} />
                  <Typography.Text type="danger" style={{ fontSize: "12px" }}>
                    Expires on: {dayjs(record.endDate).format("DD MMM YYYY")}
                  </Typography.Text>
                </Space>
              ))}
          </Flex>
        ),
    },
    {
      key: "title",
      title: "Title",
      dataIndex: "title",
      width: "20%",
      minWidth: 200,
      render: (text, record) =>
        isRowToggling(record) ? (
          <CellSkeleton width={120} />
        ) : (
          <Button
            type="link"
            onClick={() =>
              record && record._id ? navigateToTab(record._id, "details") : null
            }
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              textAlign: "left",
              height: "auto",
              padding: "4px 0",
            }}
          >
            {text}
          </Button>
        ),
    },
    {
      key: "numberOfOpenings",
      title: "Vacancies",
      dataIndex: "numberOfOpenings",
      width: "10%",
      minWidth: 100,
      render: (text, record) =>
        isRowToggling(record) ? (
          <CellSkeleton width={40} />
        ) : (
          <Button
            type="link"
            onClick={() => navigateToTab(record._id, "applications")}
          >
            {text}
          </Button>
        ),
    },
    {
      key: "numberOfApplications",
      title: "Applied",
      dataIndex: "numberOfApplications",
      width: "10%",
      minWidth: 100,
      render: (text, record) =>
        isRowToggling(record) ? (
          <CellSkeleton width={40} />
        ) : (
          <Button
            type="link"
            onClick={() => navigateToTab(record._id, "applications")}
          >
            {text}
          </Button>
        ),
    },
    {
      key: "numberOfQualifiedApplications",
      title: "Qualified",
      dataIndex: "numberOfQualifiedApplications",
      width: "10%",
      minWidth: 100,
      render: (text, record) =>
        isRowToggling(record) ? (
          <CellSkeleton width={40} />
        ) : (
          <Button
            type="link"
            onClick={() => navigateToTab(record._id, "round-qualified")}
          >
            {text}
          </Button>
        ),
    },
    // {
    //   key: "numberOfPendingInterviews",
    //   title: "Pending Interview",
    //   dataIndex: "numberOfPendingInterviews",
    //   width: "10%",
    //   minWidth: 150,
    //   render: (text, record) =>
    //     isRowToggling(record) ? (
    //       <CellSkeleton width={40} />
    //     ) : (
    //       <Button
    //         type="link"
    //         onClick={() => navigateToTab(record._id, "pending-interview")}
    //       >
    //         {text}
    //       </Button>
    //     ),
    // },
    // {
    //   key: "numberOfHiredApplications",
    //   title: "Hired",
    //   dataIndex: "numberOfHiredApplications",
    //   width: "10%",
    //   minWidth: 100,
    //   render: (text, record) =>
    //     isRowToggling(record) ? (
    //       <CellSkeleton width={40} />
    //     ) : (
    //       <Button
    //         type="link"
    //         onClick={() => navigateToTab(record._id, "hired")}
    //       >
    //         {text}
    //       </Button>
    //     ),
    // },
    // {
    //   key: "numberOfRejectedApplications",
    //   title: "Rejected",
    //   dataIndex: "numberOfRejectedApplications",
    //   width: "10%",
    //   minWidth: 100,
    //   render: (text, record) =>
    //     isRowToggling(record) ? (
    //       <CellSkeleton width={40} />
    //     ) : (
    //       <Button
    //         type="link"
    //         onClick={() => navigateToTab(record._id, "rejected")}
    //       >
    //         {text || 0}
    //       </Button>
    //     ),
    // },
    {
      key: "action",
      title: "Action",
      width: "10%",
      minWidth: 120,
      fixed: "right",
      render: (text, record) => {
        const isToggling = isRowToggling(record);
        return (
          <Flex vertical gap={4} align="flex-start">
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              checked={record.isActive}
              loading={isToggling}
              disabled={isToggling}
              onChange={(checked) => toggleJobStatus(record, checked)}
            />
            {(isActiveButPastEndDate(record) ||
              isInactiveButPastEndDate(record)) && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto", fontSize: "12px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openExtendEndDateModal(record);
                }}
              >
                Extend end date
              </Button>
            )}
          </Flex>
        );
      },
    },
  ];

  const dataSource = useMemo(() => {
    return jobPosts?.map((job: JobDescriptionEntity) => ({
      key: job?._id,
      ...job,
    }));
  }, [jobPosts]);

  useEffect(() => {
    const updateHeight = () => {
      if (
        !containerRef.current ||
        !paginationRef.current ||
        !tableWrapperRef.current
      )
        return;
      // Walk up to the flex-constrained wrapper (overflowY: hidden) to get the true available height
      const constrainedParent =
        containerRef.current.closest<HTMLElement>(
          ".team-members-page-tab-children-wrapper",
        ) || containerRef.current.parentElement;
      if (!constrainedParent) return;
      const availableH = constrainedParent.getBoundingClientRect().height;
      const paginationH = paginationRef.current.getBoundingClientRect().height;
      const theadEl =
        tableWrapperRef.current.querySelector<HTMLElement>(".ant-table-thead");
      const theadH = theadEl ? theadEl.getBoundingClientRect().height : 39;
      // Also account for filter menu above the table
      const filterMenu = constrainedParent.querySelector<HTMLElement>(
        ".job-posts-filter-menu",
      );
      const filterH = filterMenu
        ? filterMenu.getBoundingClientRect().height + 16
        : 0; // 16px gap
      setTableScrollHeight(
        Math.max(availableH - paginationH - theadH - filterH - 8, 200),
      );
    };

    const ro = new ResizeObserver(updateHeight);
    if (containerRef.current)
      ro.observe(
        containerRef.current.closest<HTMLElement>(
          ".team-members-page-tab-children-wrapper",
        ) || containerRef.current,
      );
    if (paginationRef.current) ro.observe(paginationRef.current);
    updateHeight();

    return () => ro.disconnect();
  }, []);

  // Calculate total min-width for horizontal scroll
  const totalMinWidth = columns.reduce((total, column) => {
    return total + (column.minWidth || 100);
  }, 0);

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  return (
    <section className="job-posts-table-container" ref={containerRef}>
      <div className="job-posts-table" ref={tableWrapperRef}>
        <Table
          columns={columns}
          dataSource={dataSource}
          scroll={{
            x: totalMinWidth,
            scrollToFirstRowOnChange: true,
            y: tableScrollHeight,
          }}
          size="small"
          rowKey="_id"
          pagination={false}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (togglingJobId === record._id)
              classes.push("job-posts-table-row--loading");
            if (isEndDateNear(record))
              classes.push("job-posts-table-row--expiring");
            if (isActiveButPastEndDate(record))
              classes.push("job-posts-table-row--past-end-date");
            return classes.join(" ");
          }}
        />
      </div>

      <div className="job-posts-pagination" ref={paginationRef}>
        <Pagination
          current={pagination.page}
          pageSize={pagination.limit}
          total={pagination.totalRecords}
          onChange={handlePaginationChange}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} job posts`
          }
        />
      </div>
      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Limit reached"
        type="job-post-limit"
      />

      <Modal
        title="Extend Job Post End Date"
        open={endDateModalVisible}
        onOk={handleExtendEndDate}
        onCancel={handleCancelEndDateModal}
        okText="Extend & Activate"
        cancelText="Cancel"
        confirmLoading={togglingJobId === jobToReactivate?._id}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            The job post <strong>"{jobToReactivate?.title}"</strong> has passed
            its end date (
            {jobToReactivate?.endDate
              ? dayjs(jobToReactivate.endDate).format("DD-MM-YYYY")
              : "N/A"}
            ).
          </p>
          <p>
            {jobToReactivate?.isActive
              ? "Please select a new end date to keep this job post active."
              : "Please select a new end date to reactivate this job post."}
          </p>
        </div>
        <Space direction="vertical" style={{ width: "100%" }}>
          <DatePicker
            value={newEndDate}
            onChange={(date) => setNewEndDate(date)}
            placeholder="Select new end date"
            style={{ width: "100%" }}
            format="DD-MM-YYYY"
            disabledDate={(current) => {
              return current && current < dayjs().startOf("day");
            }}
          />
          {newEndDate && (
            <p style={{ color: "#666", fontSize: "12px", marginTop: 8 }}>
              Job post will close on: {newEndDate.format("DD-MM-YYYY")}
            </p>
          )}
        </Space>
      </Modal>
    </section>
  );
};

export default JobPostTable;
